import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { SLIDES } from "@/lib/data";
import { useQuote } from "@/context/QuoteContext";

const DURATION = 6500;

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const { openQuoteModal } = useQuote();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);

  const go = (i) => setActive(((i % SLIDES.length) + SLIDES.length) % SLIDES.length);

  useEffect(() => {
    const t = setInterval(() => setActive((a) => (a + 1) % SLIDES.length), DURATION);
    return () => clearInterval(t);
  }, [active]);

  const slide = SLIDES[active];

  return (
    <section
      id="top"
      ref={sectionRef}
      className="relative w-full overflow-hidden"
      style={{ height: "92vh", minHeight: 560, maxHeight: 800, marginTop: 80 }}
      data-testid="hero-carousel"
    >
      <AnimatePresence>
        <motion.div key={active} className="absolute inset-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.8 }}>
          <picture>
            <source media="(max-width: 639px)" srcSet={slide.mobileImg} />
            <motion.img
              key={`img-${active}`}
              alt={slide.tag}
              initial={{ scale: 1.08 }}
              animate={{ scale: 1, y: imgY }}
              transition={{ scale: { duration: 7, ease: "linear" } }}
              className="absolute inset-0 w-full h-full object-cover object-center"
              src={slide.img}
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-r from-medis-navy/90 via-medis-navy/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-medis-navy/60 via-transparent to-transparent" />

          <div className="absolute inset-0 z-10 flex items-center">
            <div className="max-w-7xl w-full mx-auto px-5 sm:px-8 lg:px-10">
              <div className="max-w-2xl">
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="inline-flex items-center gap-2 rounded-full border border-medis-teal/40 bg-medis-teal/15 px-4 py-1.5 text-[11.5px] uppercase tracking-[0.2em] text-medis-teal font-semibold mb-6"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-medis-teal animate-pulse" />
                  {slide.tag}
                </motion.div>
                <h1 className="font-display font-bold text-white text-[32px] sm:text-[44px] lg:text-[54px] leading-[1.08] tracking-tight">
                  {slide.lines.map((line, i) => (
                    <span key={`${active}-${i}`} className="mask-line">
                      <motion.span
                        initial={{ y: "112%" }}
                        animate={{ y: 0 }}
                        transition={{ duration: 0.85, delay: 0.18 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                      >
                        {line}
                      </motion.span>
                    </span>
                  ))}
                </h1>
                <motion.p
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.45 }}
                  className="mt-5 text-white/70 text-[16px] sm:text-[17px] leading-relaxed max-w-xl"
                >
                  {slide.desc}
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.58 }}
                  className="mt-8 flex flex-wrap items-center gap-3"
                >
                  <button
                    onClick={() => openQuoteModal()}
                    className="group inline-flex items-center gap-2 bg-medis-teal text-white px-7 py-3.5 rounded-md text-[14.5px] font-semibold hover:bg-medis-teal/90 transition-colors"
                    data-testid="hero-cta-quote"
                  >
                    Get a Quote
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </button>
                  <a
                    href="#products"
                    className="inline-flex items-center gap-2 border border-white/25 text-white px-7 py-3.5 rounded-md text-[14.5px] font-semibold hover:border-white/50 hover:bg-white/5 transition-colors"
                    data-testid="hero-cta-explore"
                  >
                    Explore Products
                  </a>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => go(i)}
            className="transition-all duration-300 rounded-full"
            style={{ width: i === active ? 28 : 8, height: 8, background: i === active ? "#1D9E75" : "rgba(255,255,255,0.35)" }}
            data-testid={`hero-slide-indicator-${i}`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      <button
        onClick={() => go(active - 1)}
        className="hidden sm:flex absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full border border-white/20 bg-white/10 backdrop-blur items-center justify-center text-white hover:bg-white/20 transition-colors"
        data-testid="hero-carousel-prev-button"
        aria-label="Previous slide"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        onClick={() => go(active + 1)}
        className="hidden sm:flex absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full border border-white/20 bg-white/10 backdrop-blur items-center justify-center text-white hover:bg-white/20 transition-colors"
        data-testid="hero-carousel-next-button"
        aria-label="Next slide"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      <div className="absolute top-8 right-8 z-20 text-white/40 text-[12px] font-mono tracking-widest hidden sm:block">
        {String(active + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
      </div>
    </section>
  );
}
